import React, { ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import { gapsOp } from 'store/ducks/Gaps';
import { ISelectionOption } from 'store/ducks/globalTypes';
import * as Styled from './DragTextBox.styled';

interface ITextBoxProps {
  children: string | ReactNode;
  onIdChange?: (
    newId: number | null,
    dragText: string | null,
    selectedValue: string | null
  ) => void;
}

const DragTextBox: React.FC<ITextBoxProps> = ({ children, onIdChange }) => {
  const dispatch = useDispatch();
  const [selectedValue, setSelectedValue] = useState<string>('Search');
  const [options, setOptions] = useState<ISelectionOption[]>([]);
  const [hasDispatched, setHasDispatched] = useState(false);

  const { auditFindingMapping, clickedHandleMoveButton, scheme } = useSelector(
    (state: RootState) => ({
      auditFindingMapping: state.gaps.auditFindingMapping,
      clickedHandleMoveButton: state.gaps.clickedHandleMoveButton,
      scheme: state.global.scheme
    })
  );

  const handleSelectChange = (value: unknown) => {
    const stringValue = String(value);
    setSelectedValue(stringValue);
    setHasDispatched(false);

    if (onIdChange) {
      onIdChange(
        parseInt(stringValue) || null,
        children as string,
        stringValue || null
      );
    }
  };

  useEffect(() => {
    // Dispatch when selectedValue changes and is not default
    if (selectedValue && selectedValue !== 'Search' && !hasDispatched) {
      dispatch(
        gapsOp.setAssignedEls({
          id: children as string,
          txt: selectedValue
        })
      );
      setHasDispatched(true);
    }
  }, [selectedValue, children, dispatch, hasDispatched]);

  useEffect(() => {
    if (clickedHandleMoveButton) {
      setSelectedValue('Search');
      setHasDispatched(false);
      dispatch(gapsOp.setClickedHandleMoveButton(false));
    }
  }, [clickedHandleMoveButton, dispatch]);

  useEffect(() => {
    if (auditFindingMapping && auditFindingMapping.length > 0) {
      // Create options directly from auditFindingMapping without deduplication
      const newOptions = auditFindingMapping.map(
        (item: { id?: number; value?: string }) => ({
          value: item.id?.toString() || '',
          label: item.value || item.id?.toString() || ''
        })
      );
      setOptions(newOptions);
    } else {
      setOptions([]);
    }
  }, [auditFindingMapping]);

  return (
    <Styled.StyledMainDiv className='drag-text-box' scheme={scheme}>
      <div>
        <Styled.StyledSelect
          className='select-search'
          defaultActiveFirstOption
          showSearch
          optionFilterProp='children'
          virtual={false}
          filterOption={(input, option) => {
            const label = option?.label ?? '';
            if (typeof label !== 'string') {
              return false;
            }
            return (
              label.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
              label.toLowerCase().indexOf(' ' + input.toLowerCase()) >= 0 ||
              label.toLowerCase().indexOf(input.toLowerCase() + ' ') >= 0
            );
          }}
          filterSort={(optionA, optionB) =>
            (optionA?.label ?? '')
              .toLowerCase()
              .localeCompare((optionB?.label ?? '').toLowerCase())
          }
          options={options}
          onChange={handleSelectChange}
          value={selectedValue === 'Search' ? 'Search' : selectedValue}
        />
      </div>
      <Styled.StyledSpan scheme={scheme}>{children}</Styled.StyledSpan>
    </Styled.StyledMainDiv>
  );
};

export default DragTextBox;
